import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin, Play, Sparkles } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { useShowreel } from "./Showreel";
import { Reveal } from "./Reveal";
import { resolveUrl, isUploadedVideo } from "../../lib/media";
import { SectionBg } from "./SectionBg";
import { MapsWaze } from "./MapsWaze";
import { FestivalEntryTransition, prefersReducedMotion } from "./FestivalEntryTransition";
import { DEFAULT_HERO, editionVisual } from "./FestivalShowcase";
import { computeApplyState } from "../../lib/festivalApply";

export const Festival = () => {
  const { content } = useContent();
  const f = content.festival;
  const showreel = useShowreel();
  const ref = useRef(null);
  const navigate = useNavigate();
  const [entering, setEntering] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  // Transition config (admin-controlled) + current edition visual.
  const trans = (content.festivalArchive || {}).transition || {};
  const eds = ((content.festivalArchive || {}).editions || []).filter((e) => e && !e.hidden);
  const currentEd = eds.find((e) => e.current) || eds.sort((a, b) => (parseInt(b.year, 10) || 0) - (parseInt(a.year, 10) || 0))[0];
  const transImage = resolveUrl(editionVisual(currentEd)) || DEFAULT_HERO;
  const transDuration = trans.duration != null ? Number(trans.duration) : 1.1;
  const transAccent = (currentEd && currentEd.theme && currentEd.theme.primary)
    || ((content.festivalArchive || {}).theme || {}).primary || "#2e6bff";

  // Cinematic hand-off from Karams Studio to the Karams Festival universe.
  const go = () => {
    if (trans.enabled === false || prefersReducedMotion() || entering) return navigate("/festival");
    setEntering(true);
  };

  // Entry page (accueil) config — bouton texte / lien / visibilité, piloté par l'admin.
  const entry = f.entry || {};
  const primaryLabel = entry.primaryLabel || "Entrer dans l'univers du Festival";
  const secondaryLabel = entry.secondaryLabel || "Participer";
  const openLink = (link) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) return window.open(link, "_blank", "noopener");
    navigate(link);
  };
  const goPrimary = () => (entry.primaryLink ? openLink(entry.primaryLink) : go());
  const goSecondary = () => openLink(entry.secondaryLink || "/festival?apply=1");

  // Candidature scheduling → show/hide the "Participer" button automatically.
  const applyState = computeApplyState(entry); // "open" | "upcoming" | "closed"
  const secondaryVisible = entry.secondaryEnabled !== false && applyState === "open";
  const openTs = entry.applyOpen ? new Date(entry.applyOpen).getTime() : null;
  const closeTs = entry.applyClose ? new Date(entry.applyClose).getTime() : null;
  const fmtDate = (iso) => { try { const d = new Date(iso); const day = d.getDate(); return `${day === 1 ? "1er" : day} ${d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`; } catch { return ""; } };
  const applyStatusText =
    applyState === "upcoming" && openTs ? `Ouverture des candidatures le ${fmtDate(entry.applyOpen)}`
      : applyState === "closed" ? "Candidatures clôturées"
        : (applyState === "open" && entry.applySchedule && closeTs) ? `Candidatures ouvertes jusqu'au ${fmtDate(entry.applyClose)}`
          : "";
  const showApplyStatus = entry.applySchedule && entry.applyStatusVisible !== false && applyStatusText;

  const bgVideo = f.backgroundVideoUrl && isUploadedVideo(f.backgroundVideoUrl);

  return (
    <section id="festival" ref={ref} className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10 overflow-hidden" data-testid="festival-section">
      <div className="absolute inset-0 opacity-30">
        {bgVideo
          ? <video src={resolveUrl(f.backgroundVideoUrl)} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          : <motion.img style={{ y, scale: 1.2 * ((f.backgroundImageFrame || {}).zoom ?? 1), objectPosition: `${(f.backgroundImageFrame || {}).posX ?? 50}% ${(f.backgroundImageFrame || {}).posY ?? 50}%`, transformOrigin: `${(f.backgroundImageFrame || {}).posX ?? 50}% ${(f.backgroundImageFrame || {}).posY ?? 50}%` }} src={resolveUrl(f.backgroundImage)} alt="Karams Festival" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/60" />
      </div>
      <SectionBg id="festival" />

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl">
          {f.titleMode !== "logo" && (
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-electric" />
              <span className="text-xs tracking-[0.35em] uppercase text-electric">{f.eyebrow}</span>
            </div>
          )}
          {f.titleMode === "logo" && f.logo ? (
            <span className={`inline-block mb-8 ${f.logoPanel ? "rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 p-5" : ""}`}><img src={resolveUrl(f.logo)} alt={`${f.titlePrefix} ${f.titleAccent}`} data-testid="festival-title-logo" className="max-h-40 md:max-h-56 w-auto" /></span>
          ) : (
            <h2 className={`${f.titleFont === "sans" ? "font-sans font-bold" : "font-serif"} text-6xl md:text-8xl text-white tracking-tight leading-[0.9] mb-8`}>
              {f.titlePrefix} <span className="italic text-electric">{f.titleAccent}</span>
            </h2>
          )}

          {/* Infos essentielles — dates / lieu / thème, sobre et compact */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 mb-8">
            {f.dates && <span className="inline-flex items-center gap-2 text-sm text-white/80"><CalendarDays size={16} className="text-electric" /> {f.dates}</span>}
            {f.place && (f.address
              ? <MapsWaze name={f.place} address={f.address} testid="festival-maps" />
              : <span className="inline-flex items-center gap-2 text-sm text-white/80"><MapPin size={16} className="text-electric" /> {f.place}</span>)}
            {f.theme && <span className="inline-flex items-center gap-2 text-sm text-white/80"><Sparkles size={16} className="text-electric" /> {f.theme}</span>}
          </div>

          {/* Description courte */}
          {f.description && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed max-w-2xl mb-8">{f.description}</p>}

          {/* Appel à candidatures — compact, désactivable, masqué auto hors période d'ouverture */}
          {entry.callEnabled !== false && f.callForEntries && applyState === "open" && (
            <Reveal delay={0.05} className="mb-8">
              <div className="inline-flex items-start gap-3 max-w-2xl px-4 py-3 rounded-2xl border border-electric/30 bg-surface/50 backdrop-blur" data-testid="festival-call-for-entries">
                <span className="mt-0.5 shrink-0 text-[9px] tracking-[0.22em] uppercase text-black bg-electric rounded-full px-2.5 py-1">{f.callForEntriesLabel || "Appel à candidatures"}</span>
                <span className="text-sm text-white/85 font-light leading-relaxed">{f.callForEntries}</span>
              </div>
            </Reveal>
          )}

          {/* Boutons : principal (univers) + secondaire (participer) + teaser */}
          <Reveal delay={0.15}>
            <div className="flex flex-wrap items-center gap-4">
              {entry.primaryEnabled !== false && (
                <button data-testid="festival-enter-btn" data-track={primaryLabel} onClick={goPrimary}
                  className="group inline-flex items-center gap-3 px-9 py-4 bg-electric text-black rounded-full text-[13px] tracking-[0.12em] uppercase font-semibold hover:bg-white hover:glow-electric transition-[background-color,box-shadow] duration-400">
                  {primaryLabel}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              )}
              {secondaryVisible && (
                <button data-testid="festival-participate-btn" data-track={secondaryLabel} onClick={goSecondary}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-transparent text-white border border-white/25 rounded-full text-[13px] tracking-[0.12em] uppercase font-medium hover:border-electric hover:text-electric transition-[color,border-color] duration-400">
                  {secondaryLabel}
                </button>
              )}
              {f.reelUrl && (
                <button data-testid="festival-reel-btn" onClick={() => showreel.open(f.reelUrl, `${f.titlePrefix} ${f.titleAccent}`, "Bande-annonce")}
                  className="group inline-flex items-center gap-2 text-sm text-white/70 hover:text-electric transition-colors">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/25 group-hover:border-electric transition-colors"><Play size={14} className="ml-0.5" /></span>
                  Voir la bande-annonce
                </button>
              )}
            </div>
            {showApplyStatus && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/65" data-testid="festival-apply-status">
                <span className={`h-2 w-2 rounded-full ${applyState === "open" ? "bg-emerald-400" : applyState === "upcoming" ? "bg-amber-400" : "bg-white/30"}`} />
                {applyStatusText}
              </div>
            )}
          </Reveal>
        </Reveal>
      </div>

      <FestivalEntryTransition active={entering} onDone={() => navigate("/festival")} image={transImage} heroSettings={currentEd?.heroSettings} duration={transDuration} accent={transAccent} />
    </section>
  );
};
